/**
 * Core engine constants. Everything in the game is expressed in "native"
 * pixels, matching the 16-bit platformers it draws on: one tile is 16x16 native px.
 *
 * ## Resolution
 *
 * The game **always renders to a 1280x720 buffer** — 720p — with the 320x180
 * art viewport scaled up 4x inside it. That is the design target and it is
 * fixed: one number for the art scale, one for the buffer, no per-window
 * arithmetic to get wrong.
 *
 * Fitting that buffer to the display is a separate, user-selectable concern,
 * because there is a genuine trade-off and no single right answer:
 *
 * - `fill` (default) scales the 720p buffer to cover the window, fractional
 *   scale accepted. The screen is always full. On a non-multiple viewport the
 *   art is resampled very slightly by the compositor.
 * - `integer` only ever uses whole multiples of 720p (1x, 2x, 3x, ...) and
 *   letterboxes the remainder. Every art pixel stays an exact square, at the
 *   cost of black bars.
 *
 * A 1920x1080 screen is 1.5x of 720p, so `fill` is exactly right there and
 * `integer` would drop to 1x. A 1366x768 screen is not a multiple at all, so
 * `integer` gives 1x with bars and `fill` gives a slightly soft image. Hence
 * the option rather than a policy.
 */

/** Size of one tile in native pixels. */
export const TILE = 16

/** Native art viewport, in art pixels. 16:9. */
export const VIEW_W = 320
export const VIEW_H = 180

/**
 * Art scale inside the 720p buffer. 4x is exact: 320*4 = 1280, 180*4 = 720.
 */
export const DESIGN_SCALE = 4

/** The render buffer. Fixed, always 720p. */
export const DESIGN_W = VIEW_W * DESIGN_SCALE // 1280
export const DESIGN_H = VIEW_H * DESIGN_SCALE // 720

/** Simulation runs at a fixed 60Hz, like the SNES originals. */
export const FPS = 60
export const DT = 1 / FPS

/** Longest frame we will simulate in one go, to avoid spiral-of-death. */
export const MAX_FRAME_TIME = 0.25

/**
 * Standard course dimensions: 10 screens wide, 2 screens tall, in tiles.
 *
 * Deliberately NOT derived from the viewport. A course is a fixed-size
 * document, so changing the resolution must never silently truncate one — the
 * editor clamps imported levels against these numbers, and the shipped
 * courses are authored at exactly this size.
 */
export const COURSE_TILES_W = 160
export const COURSE_TILES_H = 30

/** How the 720p buffer is fitted to the display. */
export type ScalingMode = 'fill' | 'integer'

/**
 * Live view state. The buffer size and art scale are constant; only the
 * on-screen CSS size varies, and that is device-pixel-agnostic.
 */
export interface ViewState {
  /** Art scale inside the buffer. Always `DESIGN_SCALE`. */
  scale: number
  /** Buffer backing-store size. Always 1280x720. */
  width: number
  height: number
  /** Uniform scale from UI design units to buffer pixels. Always 1. */
  uiScale: number
  /** Chosen fitting policy. */
  mode: ScalingMode
  /** CSS size the canvas is displayed at, in CSS pixels. */
  displayWidth: number
  displayHeight: number
}

export const view: ViewState = {
  scale: DESIGN_SCALE,
  width: DESIGN_W,
  height: DESIGN_H,
  uiScale: 1,
  mode: 'fill',
  displayWidth: DESIGN_W,
  displayHeight: DESIGN_H,
}

export const SCALING_MODES: ScalingMode[] = ['fill', 'integer']

/**
 * Work out the CSS size to display a 1280x720 buffer at, given the space
 * available and the chosen policy.
 *
 * `fill` takes the largest fit, fractional. `integer` rounds the scale down to
 * a whole number of 720p buffers and letterboxes the rest, degrading to `fill`
 * when the window is too small for even 1x.
 */
export function fitDisplay(
  mode: ScalingMode,
  availW: number,
  availH: number,
): { w: number; h: number } {
  const ratio = DESIGN_W / DESIGN_H

  const fractional = (): { w: number; h: number } => {
    let w = Math.max(1, availW)
    let h = w / ratio
    if (h > availH) {
      h = Math.max(1, availH)
      w = h * ratio
    }
    return { w: Math.floor(w), h: Math.floor(h) }
  }

  if (mode === 'integer') {
    const k = Math.floor(Math.min(availW / DESIGN_W, availH / DESIGN_H))
    if (k >= 1) return { w: DESIGN_W * k, h: DESIGN_H * k }
    // The window is smaller than one 720p buffer, so there is no whole
    // multiple to use. Fall back to fitting rather than clipping the canvas:
    // a fractional scale is a better outcome than a cropped screen, and a mode
    // that silently clipped would be the worse bug.
    return fractional()
  }
  return fractional()
}

/** Apply the world (art) transform: native coordinates, 4x inside the buffer. */
export function applyWorldTransform(ctx: CanvasRenderingContext2D): void {
  ctx.setTransform(view.scale, 0, 0, view.scale, 0, 0)
  ctx.imageSmoothingEnabled = false
}

/** Apply the UI transform: `DESIGN_W` x `DESIGN_H` units, 1:1 into the buffer. */
export function applyUiTransform(ctx: CanvasRenderingContext2D): void {
  ctx.setTransform(view.uiScale, 0, 0, view.uiScale, 0, 0)
}

/** Reset to identity, for clearing the whole buffer. */
export function applyIdentityTransform(ctx: CanvasRenderingContext2D): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0)
}
